import React, { useState, useEffect } from 'react'
import styles from './StudentArchive.module.scss'
import {
  getStudentCases,
  getStudentCaseDetail,
  registerStudent,
  createStudentCase,
  createStudentDocument,
  updateStudentCase,
  deleteStudentCase,
  updateStudentDocument,
  deleteStudentDocument,
} from '../../../service/ArchiveService'
import { ScaleLoader } from 'react-spinners'
import Notification from '../../../utils/Notifications'

const ICO_STUDENT = <svg viewBox="0 0 24 24"><path d="M12 3L1 9l11 6 9-4.91V17h2V9M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82z"/></svg>
const ICO_DOC = <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>
const ICO_PLUS = <svg viewBox="0 0 24 24"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
const ICO_DEL = <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
const ICO_EDIT = <svg viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
const ICO_DL = <svg viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
const ICO_SEARCH = <svg viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>

const documentTypes = [
  { value: 'admission_order', label: 'Приказ о зачислении' },
  { value: 'grade_sheet', label: 'Ведомость оценок' },
  { value: 'diploma', label: 'Диплом' },
  { value: 'academic_certificate', label: 'Академическая справка' },
  { value: 'transcript', label: 'Транскрипт' },
  { value: 'medical_certificate', label: 'Медицинская справка' },
  { value: 'reference_letter', label: 'Рекомендательное письмо' },
  { value: 'other', label: 'Прочее' },
]

const getDocTypeLabel = (v) => documentTypes.find(t => t.value === v)?.label || v
const getConfLabel = (v) => ({ public: 'Открытый', internal: 'Внутренний', confidential: 'Конфиденц.', secret: 'Секретный' })[v] || v
const getConfClass = (v) => ({ public: styles.conf_public, internal: styles.conf_internal, confidential: styles.conf_confidential, secret: styles.conf_secret })[v] || ''
const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('ru-RU') } catch { return d } }

function StudentArchive() {
  const [cases, setCases] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCase, setSelectedCase] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showRegisterModal, setShowRegisterModal] = useState(false)
  const [showDocModal, setShowDocModal] = useState(false)
  const [editingDoc, setEditingDoc] = useState(null)
  const [uploadFile, setUploadFile] = useState(null)
  const [registerData, setRegisterData] = useState({ student_id: '', student_full_name: '', faculty: '', specialty: '', year_of_admission: new Date().getFullYear(), study_form: 'full_time', notes: '' })
  const [docForm, setDocForm] = useState({ document_type: 'admission_order', title: '', description: '', document_date: '', pages: 0, confidentiality_level: 'public', notes: '' })
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '', sound: '' })

  useEffect(() => { loadCases() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const loadCases = async () => {
    try {
      setLoading(true)
      const data = await getStudentCases()
      setCases(data)
    } catch { showNotify('Ошибка при загрузке дел студентов', 'error') }
    finally { setLoading(false) }
  }

  const showNotify = (message, type) => setNotify({ isOpen: true, message, type, sound: type })

  const handleSelectCase = async (caseItem) => {
    if (selectedCase?.id === caseItem.id) { setSelectedCase(null); return }
    try {
      const detail = await getStudentCaseDetail(caseItem.id)
      setSelectedCase(detail)
    } catch {
      setSelectedCase(caseItem)
    }
  }

  const handleRegister = async (e) => {
    e.preventDefault()
    try {
      const student = await registerStudent(registerData)
      const newCase = await createStudentCase({ student: student.id, case_number: `С-${student.student_id || Date.now()}`, title: `Дело студента ${student.student_full_name}`, status: 'active' })
      setCases([...cases, { ...newCase, student_full_name: student.student_full_name, student_id: student.student_id, faculty: student.faculty, specialty: student.specialty, year_of_admission: student.year_of_admission }])
      setRegisterData({ student_id: '', student_full_name: '', faculty: '', specialty: '', year_of_admission: new Date().getFullYear(), study_form: 'full_time', notes: '' })
      setShowRegisterModal(false)
      showNotify('Студент зарегистрирован, дело создано!', 'success')
    } catch { showNotify('Ошибка при регистрации студента', 'error') }
  }

  const handleDeleteCase = async (e, caseId) => {
    e.stopPropagation()
    if (!window.confirm('Удалить дело студента?')) return
    try {
      await deleteStudentCase(caseId)
      setCases(cases.filter(c => c.id !== caseId))
      if (selectedCase?.id === caseId) setSelectedCase(null)
      showNotify('Дело удалено', 'success')
    } catch { showNotify('Ошибка при удалении дела', 'error') }
  }

  const handleDigitizeCase = async (e, caseItem) => {
    e.stopPropagation()
    try {
      const upd = await updateStudentCase(caseItem.id, { is_digitized: !caseItem.is_digitized })
      setCases(cases.map(c => c.id === caseItem.id ? { ...c, ...upd } : c))
      if (selectedCase?.id === caseItem.id) setSelectedCase(prev => ({ ...prev, ...upd }))
    } catch { showNotify('Ошибка', 'error') }
  }

  const openCreateDoc = () => {
    setEditingDoc(null)
    setDocForm({ document_type: 'admission_order', title: '', description: '', document_date: '', pages: 0, confidentiality_level: 'public', notes: '' })
    setUploadFile(null)
    setShowDocModal(true)
  }

  const openEditDoc = (doc) => {
    setEditingDoc(doc)
    setDocForm({ document_type: doc.document_type, title: doc.title, description: doc.description || '', document_date: doc.document_date || '', pages: doc.pages || 0, confidentiality_level: doc.confidentiality_level || 'public', notes: doc.notes || '' })
    setShowDocModal(true)
  }

  const handleCreateDocument = async (e) => {
    e.preventDefault()
    try {
      const data = { ...docForm, case: selectedCase.id }
      const n = await createStudentDocument(data, uploadFile)
      const updDocs = [...(selectedCase.documents || []), n]
      setSelectedCase({ ...selectedCase, documents: updDocs })
      setCases(cases.map(c => c.id === selectedCase.id ? { ...c, documents: updDocs } : c))
      setDocForm({ document_type: 'admission_order', title: '', description: '', document_date: '', pages: 0, confidentiality_level: 'public', notes: '' })
      setUploadFile(null)
      setShowDocModal(false)
      showNotify('Документ добавлен!', 'success')
    } catch { showNotify('Ошибка при добавлении документа', 'error') }
  }

  const handleUpdateDocument = async (e) => {
    e.preventDefault()
    try {
      const upd = await updateStudentDocument(editingDoc.id, docForm)
      const updDocs = (selectedCase.documents || []).map(d => d.id === editingDoc.id ? upd : d)
      setSelectedCase({ ...selectedCase, documents: updDocs })
      setCases(cases.map(c => c.id === selectedCase.id ? { ...c, documents: updDocs } : c))
      setEditingDoc(null); setShowDocModal(false)
      showNotify('Документ обновлён!', 'success')
    } catch { showNotify('Ошибка при обновлении документа', 'error') }
  }

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Удалить документ?')) return
    try {
      await deleteStudentDocument(docId)
      const updDocs = (selectedCase.documents || []).filter(d => d.id !== docId)
      setSelectedCase({ ...selectedCase, documents: updDocs })
      setCases(cases.map(c => c.id === selectedCase.id ? { ...c, documents: updDocs } : c))
      showNotify('Документ удалён', 'success')
    } catch { showNotify('Ошибка при удалении документа', 'error') }
  }

  const filteredCases = cases.filter(c => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (c.student_full_name || '').toLowerCase().includes(q)
      || (c.student_id || '').toLowerCase().includes(q)
      || (c.faculty || '').toLowerCase().includes(q)
      || (c.case_number || '').toLowerCase().includes(q)
  })

  if (loading) {
    return <div className={styles.loading_container}><ScaleLoader color="#FF6B35" /></div>
  }

  return (
    <>
      <Notification notify={notify} setNotify={setNotify} />

      {showRegisterModal && (
        <div className={styles.modal_overlay} onClick={() => setShowRegisterModal(false)}>
          <div className={styles.modal_box} onClick={e => e.stopPropagation()}>
            <button className={styles.modal_close} onClick={() => setShowRegisterModal(false)}>×</button>
            <h2>Регистрация нового студента</h2>
            <form onSubmit={handleRegister}>
              <div className={styles.form_field}>
                <label>ФИО студента *</label>
                <input type="text" placeholder="Иванов Иван Иванович" required value={registerData.student_full_name}
                  onChange={e => setRegisterData({ ...registerData, student_full_name: e.target.value })} />
              </div>
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <label>№ студенческого *</label>
                  <input type="text" placeholder="СТ-2024-001" required value={registerData.student_id}
                    onChange={e => setRegisterData({ ...registerData, student_id: e.target.value })} />
                </div>
                <div className={styles.form_field}>
                  <label>Год поступления</label>
                  <input type="number" value={registerData.year_of_admission}
                    onChange={e => setRegisterData({ ...registerData, year_of_admission: parseInt(e.target.value) })} />
                </div>
              </div>
              <div className={styles.form_field}>
                <label>Факультет *</label>
                <input type="text" placeholder="Факультет информационных технологий" required value={registerData.faculty}
                  onChange={e => setRegisterData({ ...registerData, faculty: e.target.value })} />
              </div>
              <div className={styles.form_field}>
                <label>Специальность</label>
                <input type="text" placeholder="Информационные системы" value={registerData.specialty}
                  onChange={e => setRegisterData({ ...registerData, specialty: e.target.value })} />
              </div>
              <div className={styles.form_field}>
                <label>Форма обучения</label>
                <select value={registerData.study_form} onChange={e => setRegisterData({ ...registerData, study_form: e.target.value })}>
                  <option value="full_time">Очная</option>
                  <option value="part_time">Заочная</option>
                  <option value="evening">Вечерняя</option>
                </select>
              </div>
              <div className={styles.form_field}>
                <label>Примечания</label>
                <textarea placeholder="Дополнительные заметки" value={registerData.notes}
                  onChange={e => setRegisterData({ ...registerData, notes: e.target.value })} />
              </div>
              <div className={styles.form_actions}>
                <button type="submit" className={styles.btn_save}>Зарегистрировать</button>
                <button type="button" className={styles.btn_cancel} onClick={() => setShowRegisterModal(false)}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDocModal && (
        <div className={styles.modal_overlay} onClick={() => setShowDocModal(false)}>
          <div className={styles.modal_box} onClick={e => e.stopPropagation()}>
            <button className={styles.modal_close} onClick={() => setShowDocModal(false)}>×</button>
            <h2>{editingDoc ? 'Редактирование документа' : 'Добавление документа'}</h2>
            <form onSubmit={editingDoc ? handleUpdateDocument : handleCreateDocument}>
              <div className={styles.form_field}>
                <label>Тип документа *</label>
                <select required value={docForm.document_type} onChange={e => setDocForm({ ...docForm, document_type: e.target.value })}>
                  {documentTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className={styles.form_field}>
                <label>Название *</label>
                <input type="text" placeholder="Название документа" required value={docForm.title}
                  onChange={e => setDocForm({ ...docForm, title: e.target.value })} />
              </div>
              <div className={styles.form_field}>
                <label>Описание</label>
                <textarea placeholder="Краткое описание" value={docForm.description}
                  onChange={e => setDocForm({ ...docForm, description: e.target.value })} />
              </div>
              <div className={styles.form_row}>
                <div className={styles.form_field}>
                  <label>Дата документа *</label>
                  <input type="date" required value={docForm.document_date}
                    onChange={e => setDocForm({ ...docForm, document_date: e.target.value })} />
                </div>
                <div className={styles.form_field}>
                  <label>Страниц</label>
                  <input type="number" placeholder="0" value={docForm.pages}
                    onChange={e => setDocForm({ ...docForm, pages: parseInt(e.target.value) || 0 })} />
                </div>
              </div>
              <div className={styles.form_field}>
                <label>Конфиденциальность</label>
                <select value={docForm.confidentiality_level} onChange={e => setDocForm({ ...docForm, confidentiality_level: e.target.value })}>
                  <option value="public">Открытый</option>
                  <option value="internal">Внутренний</option>
                  <option value="confidential">Конфиденциальный</option>
                  <option value="secret">Секретный</option>
                </select>
              </div>
              <div className={styles.form_field}>
                <label>Примечания</label>
                <textarea placeholder="Дополнительные заметки" value={docForm.notes}
                  onChange={e => setDocForm({ ...docForm, notes: e.target.value })} />
              </div>
              {!editingDoc && (
                <div className={styles.form_field}>
                  <label>Файл документа</label>
                  <input type="file" onChange={e => setUploadFile(e.target.files[0])} />
                </div>
              )}
              <div className={styles.form_actions}>
                <button type="submit" className={styles.btn_save}>{editingDoc ? 'Сохранить' : 'Добавить'}</button>
                <button type="button" className={styles.btn_cancel} onClick={() => setShowDocModal(false)}>Отмена</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className={styles.student_archive}>
        <div className={styles.arch_header}>
          <div className={styles.arch_header_icon}>{ICO_STUDENT}</div>
          <div className={styles.arch_header_text}>
            <h1>Архив студентов</h1>
            <p>Личные дела и документы студентов университета</p>
          </div>
          <button className={styles.btn_create} onClick={() => setShowRegisterModal(true)}>{ICO_PLUS} Регистрация</button>
        </div>

        <div className={styles.search_bar}>
          {ICO_SEARCH}
          <input
            className={styles.search_input}
            type="text"
            placeholder="Поиск по ФИО, № студента, факультету, № дела..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        <div className={styles.table_section}>
          <div className={styles.section_head}>
            <h2>{ICO_STUDENT} Дела студентов ({filteredCases.length})</h2>
            <button className={styles.btn_create} onClick={() => setShowRegisterModal(true)}>{ICO_PLUS} Регистрация</button>
          </div>
          <table className={styles.data_table}>
            <thead>
              <tr>
                <th>ФИО студента</th><th>№ студента</th><th>Факультет</th><th>Специальность</th><th>Год поступления</th><th>№ Дела</th><th>Статус</th><th>Оцифровано</th><th>Документов</th><th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredCases.length === 0 ? (
                <tr className={styles.empty_row}><td colSpan={10}>
                  {searchQuery ? 'Студенты не найдены по вашему запросу.' : 'Нет зарегистрированных студентов. Нажмите «Регистрация».'}
                </td></tr>
              ) : filteredCases.map(c => (
                <tr key={c.id} className={selectedCase?.id === c.id ? styles.row_selected : ''}
                  onClick={() => handleSelectCase(c)}>
                  <td className={styles.td_name}>{c.student_full_name || c.student?.student_full_name || '—'}</td>
                  <td className={styles.td_id}>{c.student_id || c.student?.student_id || '—'}</td>
                  <td className={styles.td_muted}>{c.faculty || c.student?.faculty || '—'}</td>
                  <td className={styles.td_muted}>{c.specialty || c.student?.specialty || '—'}</td>
                  <td className={styles.td_center}>{c.year_of_admission || c.student?.year_of_admission || '—'}</td>
                  <td className={styles.td_id}>{c.case_number}</td>
                  <td>
                    <span className={`${styles.status_badge} ${c.status === 'active' ? styles.status_active : c.status === 'closed' ? styles.status_closed : styles.status_inactive}`}>
                      {c.status === 'active' ? 'Активно' : c.status === 'closed' ? 'Закрыто' : c.status}
                    </span>
                  </td>
                  <td className={styles.td_center}>
                    {c.is_digitized
                      ? <span className={styles.digitized_badge}>✓ Да</span>
                      : <span style={{ color: '#AAAAAA', fontSize: '0.8rem', fontFamily: 'Nunito,sans-serif' }}>Нет</span>}
                  </td>
                  <td className={styles.td_center}>{(c.documents || []).length}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className={styles.td_actions}>
                      <button className={`${styles.btn_row} ${c.is_digitized ? styles.btn_row_blue : styles.btn_row_green}`}
                        onClick={e => handleDigitizeCase(e, c)}>
                        {c.is_digitized ? 'Снять' : 'Оцифровать'}
                      </button>
                      <button className={`${styles.btn_row} ${styles.btn_row_red}`}
                        onClick={e => handleDeleteCase(e, c.id)}>{ICO_DEL}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedCase && (
          <div className={styles.docs_section}>
            <div className={styles.docs_head}>
              <h2>{ICO_DOC} Документы дела — {selectedCase.student_full_name || selectedCase.student?.student_full_name || selectedCase.case_number}</h2>
              <button className={styles.btn_create} onClick={openCreateDoc}>{ICO_PLUS} Добавить</button>
            </div>
            <div className={styles.case_info_bar}>
              <div className={styles.info_chip}><span className={styles.info_chip_label}>№ дела</span><span className={styles.info_chip_value}>{selectedCase.case_number}</span></div>
              {(selectedCase.faculty || selectedCase.student?.faculty) && <div className={styles.info_chip}><span className={styles.info_chip_label}>Факультет</span><span className={styles.info_chip_value}>{selectedCase.faculty || selectedCase.student?.faculty}</span></div>}
              {(selectedCase.specialty || selectedCase.student?.specialty) && <div className={styles.info_chip}><span className={styles.info_chip_label}>Специальность</span><span className={styles.info_chip_value}>{selectedCase.specialty || selectedCase.student?.specialty}</span></div>}
              {(selectedCase.year_of_admission || selectedCase.student?.year_of_admission) && <div className={styles.info_chip}><span className={styles.info_chip_label}>Год поступления</span><span className={styles.info_chip_value}>{selectedCase.year_of_admission || selectedCase.student?.year_of_admission}</span></div>}
              <div className={styles.info_chip}><span className={styles.info_chip_label}>Оцифровано</span><span className={styles.info_chip_value}>{selectedCase.is_digitized ? 'Да' : 'Нет'}</span></div>
            </div>
            <table className={styles.data_table}>
              <thead>
                <tr>
                  <th>Тип документа</th><th>Название</th><th>Описание</th><th>Дата</th><th>Стр.</th><th>Конфиденц.</th><th>Файлы</th><th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {(!selectedCase.documents || selectedCase.documents.length === 0) ? (
                  <tr className={styles.empty_row}><td colSpan={8}>Документов нет. Нажмите «Добавить».</td></tr>
                ) : selectedCase.documents.map(doc => (
                  <tr key={doc.id}>
                    <td><span className={styles.doc_type_badge}>{getDocTypeLabel(doc.document_type)}</span></td>
                    <td className={styles.td_name}>{doc.title}</td>
                    <td className={styles.td_muted}>{doc.description || '—'}</td>
                    <td>{fmtDate(doc.document_date)}</td>
                    <td className={styles.td_center}>{doc.pages > 0 ? doc.pages : '—'}</td>
                    <td><span className={`${styles.confidentiality_badge} ${getConfClass(doc.confidentiality_level)}`}>{getConfLabel(doc.confidentiality_level)}</span></td>
                    <td>
                      {doc.files?.length > 0 ? (
                        <div className={styles.file_links}>
                          {doc.files.map(f => (
                            <a key={f.id} href={f.file} download={f.file_name} className={styles.file_download_link}>
                              {ICO_DL} {f.file_name || 'Скачать'}
                            </a>
                          ))}
                        </div>
                      ) : '—'}
                    </td>
                    <td>
                      <div className={styles.td_actions}>
                        <button className={`${styles.btn_row} ${styles.btn_row_dark}`} onClick={() => openEditDoc(doc)}>{ICO_EDIT} Ред.</button>
                        <button className={`${styles.btn_row} ${styles.btn_row_red}`} onClick={() => handleDeleteDocument(doc.id)}>{ICO_DEL}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

export default StudentArchive
